import './style.css';
import { GameEngine, dropIntervalFor } from './game/engine';
import { LockDelayController } from './game/lock-delay';
import { cellsFor, PIECE_COLORS } from './game/pieces';
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type ActivePiece,
  type Board,
  type GameStatus,
  type PieceKind,
} from './game/types';
import { SoundPlayer } from './sound';
import { loadGame, loadSettings, saveGame, saveSettings } from './storage';

const BOARD_PIXEL_WIDTH = 300;
const BOARD_PIXEL_HEIGHT = 540;
const CELL_SIZE = BOARD_PIXEL_WIDTH / BOARD_WIDTH;

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const boardCanvas = element<HTMLCanvasElement>('gameCanvas');
const nextCanvas = element<HTMLCanvasElement>('nextCanvas');
const scoreValue = element<HTMLElement>('scoreValue');
const levelValue = element<HTMLElement>('levelValue');
const linesValue = element<HTMLElement>('linesValue');
const bestValue = element<HTMLElement>('bestValue');
const overlay = element<HTMLElement>('gameOverlay');
const overlayEyebrow = element<HTMLElement>('overlayEyebrow');
const overlayTitle = element<HTMLElement>('overlayTitle');
const overlayText = element<HTMLElement>('overlayText');
const startButton = element<HTMLButtonElement>('startButton');
const pauseButton = element<HTMLButtonElement>('pauseButton');
const soundButton = element<HTMLButtonElement>('soundButton');
const undoButton = element<HTMLButtonElement>('undoButton');
const toast = element<HTMLElement>('toast');

const settings = loadSettings();
const engine = new GameEngine(Math.random, loadGame());
const sound = new SoundPlayer(settings.sound);
const lockDelay = new LockDelayController();
let previousLines = engine.state.lines;
let previousStatus = engine.state.status;
let lastDropAt = performance.now();
let toastTimer: number | undefined;
let renderFrame: number | undefined;
let persistTimer: number | undefined;

function setCanvasResolution(canvas: HTMLCanvasElement, width: number, height: number): void {
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  const context = canvas.getContext('2d');
  context?.setTransform(ratio, 0, 0, ratio, 0, 0);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.arcTo(x + width, y, x + width, y + safeRadius, safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.arcTo(x + width, y + height, x + width - safeRadius, y + height, safeRadius);
  context.lineTo(x + safeRadius, y + height);
  context.arcTo(x, y + height, x, y + height - safeRadius, safeRadius);
  context.lineTo(x, y + safeRadius);
  context.arcTo(x, y, x + safeRadius, y, safeRadius);
  context.closePath();
}

function drawBlock(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  kind: PieceKind,
  size = CELL_SIZE,
  glow = true,
): void {
  const color = PIECE_COLORS[kind];
  const inset = Math.max(1.6, size * 0.065);
  const radius = Math.max(3, size * 0.17);
  const px = x * size + inset;
  const py = y * size + inset;
  const blockSize = size - inset * 2;

  context.save();
  if (glow) {
    context.shadowColor = color.glow;
    context.shadowBlur = size * 0.24;
  }
  roundedRect(context, px, py, blockSize, blockSize, radius);
  context.fillStyle = color.fill;
  context.fill();

  const highlight = context.createLinearGradient(px, py, px, py + blockSize);
  highlight.addColorStop(0, 'rgba(255,255,255,0.34)');
  highlight.addColorStop(0.45, 'rgba(255,255,255,0.03)');
  highlight.addColorStop(1, 'rgba(0,0,0,0.18)');
  roundedRect(context, px, py, blockSize, blockSize, radius);
  context.fillStyle = highlight;
  context.fill();
  context.restore();
}

function drawGrid(context: CanvasRenderingContext2D): void {
  context.clearRect(0, 0, BOARD_PIXEL_WIDTH, BOARD_PIXEL_HEIGHT);
  const background = context.createLinearGradient(0, 0, 0, BOARD_PIXEL_HEIGHT);
  background.addColorStop(0, '#18203a');
  background.addColorStop(1, '#10162a');
  context.fillStyle = background;
  context.fillRect(0, 0, BOARD_PIXEL_WIDTH, BOARD_PIXEL_HEIGHT);
  context.strokeStyle = 'rgba(202,216,255,0.12)';
  context.lineWidth = 1;
  for (let x = 1; x < BOARD_WIDTH; x += 1) {
    context.beginPath();
    context.moveTo(x * CELL_SIZE + 0.5, 0);
    context.lineTo(x * CELL_SIZE + 0.5, BOARD_PIXEL_HEIGHT);
    context.stroke();
  }
  for (let y = 1; y < BOARD_HEIGHT; y += 1) {
    context.beginPath();
    context.moveTo(0, y * CELL_SIZE + 0.5);
    context.lineTo(BOARD_PIXEL_WIDTH, y * CELL_SIZE + 0.5);
    context.stroke();
  }
}

function drawBoard(board: Board, active: ActivePiece | null): void {
  const context = boardCanvas.getContext('2d');
  if (!context) return;
  drawGrid(context);
  board.forEach((row, y) => row.forEach((cell, x) => {
    if (cell) drawBlock(context, x, y, cell, CELL_SIZE, false);
  }));
  if (active) {
    for (const cell of cellsFor(active.kind, active.rotation)) {
      const y = active.y + cell.y;
      if (y >= 0) drawBlock(context, active.x + cell.x, y, active.kind);
    }
  }
}

function drawNext(kind: PieceKind): void {
  const context = nextCanvas.getContext('2d');
  if (!context) return;
  const size = 22;
  context.clearRect(0, 0, 112, 112);
  const cells = cellsFor(kind, 0);
  const minX = Math.min(...cells.map((cell) => cell.x));
  const maxX = Math.max(...cells.map((cell) => cell.x));
  const minY = Math.min(...cells.map((cell) => cell.y));
  const maxY = Math.max(...cells.map((cell) => cell.y));
  const width = (maxX - minX + 1) * size;
  const height = (maxY - minY + 1) * size;
  const offsetX = (112 - width) / 2 - minX * size;
  const offsetY = (112 - height) / 2 - minY * size;
  for (const cell of cells) {
    drawBlock(context, (offsetX + cell.x * size) / size, (offsetY + cell.y * size) / size, kind, size);
  }
}

function updateOverlay(): void {
  const { status, score } = engine.state;
  overlay.classList.toggle('visible', status !== 'playing');
  pauseButton.disabled = status === 'idle' || status === 'over';

  if (status === 'idle') {
    overlayEyebrow.textContent = '准备好了吗';
    overlayTitle.textContent = '经典玩法，轻装上阵';
    overlayText.textContent = '左右滑动移动，下滑加速，点击旋转。游戏会自动保存在这台设备上。';
    startButton.textContent = '开始游戏';
  } else if (status === 'paused') {
    overlayEyebrow.textContent = '进度已保存';
    overlayTitle.textContent = '游戏暂停';
    overlayText.textContent = '休息一下，回来时从当前位置继续。';
    startButton.textContent = '继续游戏';
  } else if (status === 'over') {
    overlayEyebrow.textContent = `本局得分 ${score.toLocaleString('zh-CN')}`;
    overlayTitle.textContent = '再来一局？';
    overlayText.textContent = '每一次落块，都会让判断更精准。';
    startButton.textContent = '重新开始';
  }
}

function updateSoundButton(): void {
  soundButton.setAttribute('aria-pressed', String(settings.sound));
  soundButton.setAttribute('aria-label', settings.sound ? '关闭声音' : '开启声音');
  soundButton.classList.toggle('muted', !settings.sound);
}

function updateUndoButton(): void {
  undoButton.disabled = !engine.canUndo;
  undoButton.setAttribute('aria-label', engine.canUndo ? '返回上次落块前' : '暂无可反悔操作');
}

function renderNow(): void {
  const state = engine.state;
  settings.bestScore = Math.max(settings.bestScore, state.score);
  scoreValue.textContent = state.score.toLocaleString('zh-CN');
  levelValue.textContent = String(state.level);
  linesValue.textContent = String(state.lines);
  bestValue.textContent = settings.bestScore.toLocaleString('zh-CN');
  drawBoard(state.board, state.active);
  drawNext(state.next);
  updateOverlay();
  updateSoundButton();
  updateUndoButton();
}

function render(): void {
  if (renderFrame !== undefined) return;
  renderFrame = window.requestAnimationFrame(() => {
    renderFrame = undefined;
    renderNow();
  });
}

function persistNow(): void {
  if (persistTimer !== undefined) window.clearTimeout(persistTimer);
  persistTimer = undefined;
  saveGame(engine.state);
  saveSettings(settings);
}

function persist(): void {
  if (persistTimer !== undefined) window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(persistNow, 250);
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.classList.add('visible');
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove('visible'), 2400);
}

function haptic(): void {
  if (typeof navigator.vibrate === 'function') navigator.vibrate(8);
}

function afterAction(actionSound?: 'move' | 'rotate' | 'lock'): void {
  if (actionSound) sound.play(actionSound);
  haptic();
  render();
  persist();
}

function perform(action: 'left' | 'right' | 'rotate' | 'down'): void {
  if (engine.state.status !== 'playing') return;
  if (action === 'left' && engine.move(-1)) {
    lockDelay.adjusted(performance.now(), engine.isGrounded());
    afterAction('move');
  } else if (action === 'right' && engine.move(1)) {
    lockDelay.adjusted(performance.now(), engine.isGrounded());
    afterAction('move');
  } else if (action === 'rotate' && engine.rotate()) {
    lockDelay.adjusted(performance.now(), engine.isGrounded());
    afterAction('rotate');
  } else if (action === 'down') {
    const moved = engine.softDrop();
    lockDelay.observe(performance.now(), engine.isGrounded());
    if (moved) afterAction();
  }
}

startButton.addEventListener('click', () => {
  if (engine.state.status === 'paused') engine.togglePause();
  else engine.start();
  previousLines = engine.state.lines;
  previousStatus = engine.state.status;
  lastDropAt = performance.now();
  lockDelay.resetPiece();
  void sound.unlock();
  sound.play('start');
  renderNow();
  persistNow();
});

pauseButton.addEventListener('click', () => {
  engine.togglePause();
  renderNow();
  persistNow();
});

soundButton.addEventListener('click', () => {
  settings.sound = !settings.sound;
  sound.enabled = settings.sound;
  if (settings.sound) {
    void sound.unlock();
    sound.play('start');
  }
  updateSoundButton();
  persistNow();
});

undoButton.addEventListener('click', () => {
  if (!engine.undoLastLock()) return;
  const now = performance.now();
  previousLines = engine.state.lines;
  previousStatus = engine.state.status;
  lastDropAt = now;
  lockDelay.resetPiece();
  lockDelay.observe(now, engine.isGrounded());
  sound.play('move');
  haptic();
  showToast('已返回上次落块前');
  renderNow();
  persistNow();
});

document.addEventListener('keydown', (event) => {
  const keyActions: Record<string, 'left' | 'right' | 'rotate' | 'down'> = {
    ArrowLeft: 'left',
    ArrowRight: 'right',
    ArrowUp: 'rotate',
    ArrowDown: 'down',
    ' ': 'rotate',
  };
  const action = keyActions[event.key];
  if (action) {
    event.preventDefault();
    perform(action);
  } else if (event.key.toLowerCase() === 'p') {
    engine.togglePause();
    renderNow();
    persistNow();
  }
});

interface Gesture {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  moved: boolean;
}

let gesture: Gesture | undefined;
function beginGesture(pointerId: number, clientX: number, clientY: number): void {
  if (engine.state.status !== 'playing') return;
  gesture = {
    pointerId,
    startX: clientX,
    startY: clientY,
    lastX: clientX,
    lastY: clientY,
    moved: false,
  };
}

function moveGesture(pointerId: number, clientX: number, clientY: number): void {
  if (!gesture || gesture.pointerId !== pointerId) return;
  const cellOnScreen = boardCanvas.getBoundingClientRect().width / BOARD_WIDTH;
  const deltaX = clientX - gesture.lastX;
  const deltaY = clientY - gesture.lastY;
  if (Math.abs(deltaX) >= cellOnScreen * 0.72) {
    perform(deltaX > 0 ? 'right' : 'left');
    gesture.lastX = clientX;
    gesture.moved = true;
  }
  if (deltaY >= cellOnScreen * 0.72) {
    perform('down');
    gesture.lastY = clientY;
    gesture.moved = true;
  }
}

function endGesture(pointerId: number, clientX: number, clientY: number): void {
  if (!gesture || gesture.pointerId !== pointerId) return;
  const distance = Math.hypot(clientX - gesture.startX, clientY - gesture.startY);
  if (!gesture.moved && distance < 12) perform('rotate');
  gesture = undefined;
}

if ('PointerEvent' in window) {
  boardCanvas.addEventListener('pointerdown', (event) => {
    try {
      boardCanvas.setPointerCapture(event.pointerId);
    } catch {
      // Gesture tracking still works without capture for a simple tap.
    }
    beginGesture(event.pointerId, event.clientX, event.clientY);
  });
  boardCanvas.addEventListener('pointermove', (event) => {
    moveGesture(event.pointerId, event.clientX, event.clientY);
  });
  boardCanvas.addEventListener('pointerup', (event) => {
    endGesture(event.pointerId, event.clientX, event.clientY);
  });
  boardCanvas.addEventListener('pointercancel', () => {
    gesture = undefined;
  });
} else {
  const changedTouch = (event: TouchEvent): Touch | undefined => (
    gesture
      ? Array.from(event.changedTouches).find((touch) => touch.identifier === gesture?.pointerId)
      : event.changedTouches[0]
  );
  boardCanvas.addEventListener('touchstart', (event) => {
    const touch = changedTouch(event);
    if (!touch) return;
    event.preventDefault();
    beginGesture(touch.identifier, touch.clientX, touch.clientY);
  }, { passive: false });
  boardCanvas.addEventListener('touchmove', (event) => {
    const touch = changedTouch(event);
    if (!touch) return;
    event.preventDefault();
    moveGesture(touch.identifier, touch.clientX, touch.clientY);
  }, { passive: false });
  boardCanvas.addEventListener('touchend', (event) => {
    const touch = changedTouch(event);
    if (touch) endGesture(touch.identifier, touch.clientX, touch.clientY);
  });
  boardCanvas.addEventListener('touchcancel', () => {
    gesture = undefined;
  });
}

function gameLoop(now: number): void {
  if (engine.state.status === 'playing' && now - lastDropAt >= dropIntervalFor(engine.state.level)) {
    const moved = engine.tick();
    lastDropAt = now;
    lockDelay.observe(now, engine.isGrounded());
    if (moved) {
      render();
      persist();
    }
  }
  if (engine.state.status === 'playing' && lockDelay.shouldLock(now, engine.isGrounded()) && engine.lock()) {
    sound.play('lock');
    if (engine.state.lines > previousLines) {
      sound.play('clear');
      document.body.classList.add('line-flash');
      window.setTimeout(() => document.body.classList.remove('line-flash'), 180);
    }
    const currentStatus = engine.state.status as GameStatus;
    if (currentStatus === 'over' && previousStatus !== 'over') sound.play('over');
    previousLines = engine.state.lines;
    previousStatus = engine.state.status;
    lastDropAt = now;
    lockDelay.resetPiece();
    renderNow();
    persistNow();
  }
  requestAnimationFrame(gameLoop);
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden && engine.state.status === 'playing') {
    engine.togglePause();
    renderNow();
    persistNow();
  } else if (!document.hidden) {
    void sound.unlock();
  }
});

window.addEventListener('resize', () => {
  setCanvasResolution(boardCanvas, BOARD_PIXEL_WIDTH, BOARD_PIXEL_HEIGHT);
  setCanvasResolution(nextCanvas, 112, 112);
  renderNow();
});

window.addEventListener('offline', () => showToast('已进入离线模式，仍可继续游戏'));
window.addEventListener('online', () => showToast('网络已恢复'));

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('./sw.js').catch(() => {
      showToast('离线组件暂未启用');
    });
  });
}

setCanvasResolution(boardCanvas, BOARD_PIXEL_WIDTH, BOARD_PIXEL_HEIGHT);
setCanvasResolution(nextCanvas, 112, 112);
renderNow();
requestAnimationFrame(gameLoop);
